import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  RecoverOptions,
  SendActivationEmailOptions,
  SendRecoveryEmailOptions,
  StateMachineResponse,
  recover as universalRecover,
  sendActivationEmail as universalSendActivationEmail,
  sendRecoveryEmail as universalSendRecoveryEmail,
} from './iam-recovery-v1-recovery-recovery.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/identity' };

export function sendRecoveryEmail(
  httpClient: HttpClient
): SendRecoveryEmailSignature {
  return (email: string, options?: SendRecoveryEmailOptions) =>
    universalSendRecoveryEmail(
      email,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SendRecoveryEmailSignature {
  /**
   * Sends an email to a member with a unique link to a Wix-managed password reset page.
   * The member can use this page to reset their site password.
   * If the site owner uses this page, it resets their Wix account password instead.
   *
   * > **Note:** You must publish the Wix site connected to your Headless project.
   * > Wix uses the published site to display the password reset page. To do this, open your project dashboard,
   * > select **Design Site** from the left panel, and then select **Publish**.
   * @param - Email address associated with the account to recover.
   */
  (email: string, options?: SendRecoveryEmailOptions): Promise<void>;
}

export function sendActivationEmail(
  httpClient: HttpClient
): SendActivationEmailSignature {
  return (identityId: string, options?: SendActivationEmailOptions) =>
    universalSendActivationEmail(
      identityId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SendActivationEmailSignature {
  /**
   * Sends an activation email to the specified member.
   *
   * The email contains an activation link that the member clicks to activate
   * their account, completing the transition from an initial contact to an
   * active site member.
   * @param - Identity ID of the member to activate.
   */
  (identityId: string, options?: SendActivationEmailOptions): Promise<void>;
}

export function recover(httpClient: HttpClient): RecoverSignature {
  return (recoveryToken: string, options?: RecoverOptions) =>
    universalRecover(
      recoveryToken,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RecoverSignature {
  /**
   * Sets a new password for the member associated with the specified recovery token.
   *
   * The recovery token comes from the link in a recovery email, sent using
   * Send Recovery Email. After a successful recovery, the member can sign in
   * with the new password.
   * @param - Recovery token from the link in the recovery email.
   */
  (recoveryToken: string, options?: RecoverOptions): Promise<
    NonNullablePaths<
      StateMachineResponse,
      | `state`
      | `identity.connections`
      | `identity.connections.${number}.idpConnection.idpConnectionId`
      | `identity.connections.${number}.idpConnection.idpUserId`
      | `identity.connections.${number}.authenticatorConnection.authenticatorConnectionId`
      | `identity.connections.${number}.authenticatorConnection.reEnrollmentRequired`
      | `identity.identityProfile.emails`
      | `identity.identityProfile.phones`
      | `identity.identityProfile.labels`
      | `identity.identityProfile.privacyStatus`
      | `identity.identityProfile.customFields`
      | `identity.identityProfile.customFields.${number}.name`
      | `identity.identityProfile.customFields.${number}.value.strValue`
      | `identity.identityProfile.customFields.${number}.value.numValue`
      | `identity.identityProfile.customFields.${number}.value.boolValue`
      | `identity.identityProfile.secondaryEmails`
      | `identity.identityProfile.secondaryEmails.${number}.email`
      | `identity.identityProfile.secondaryEmails.${number}.tag`
      | `identity.identityProfile.phonesV2`
      | `identity.identityProfile.phonesV2.${number}.phone`
      | `identity.identityProfile.phonesV2.${number}.tag`
      | `identity.identityProfile.addresses`
      | `identity.identityProfile.addresses.${number}.tag`
      | `identity.email.address`
      | `identity.email.isVerified`
      | `identity.status.name`
      | `identity.status.reasons`
      | `identity.factors`
      | `identity.factors.${number}.factorId`
      | `identity.factors.${number}.type`
      | `identity.factors.${number}.status`,
      5
    >
  >;
}

export {
  Address,
  AddressTag,
  AddressWrapper,
  AuthenticatorConnection,
  Connection,
  ConnectionTypeOneOf,
  CreateRecoveryTokenRequest,
  CreateRecoveryTokenResponse,
  CustomField,
  CustomValue,
  CustomValueValueOneOf,
  Email,
  EmailOptions,
  EmailTag,
  Factor,
  FactorStatus,
  FactorType,
  Identity,
  IdentityProfile,
  IdpConnection,
  ListValue,
  MapValue,
  Metadata,
  MfaChallengeData,
  MfaReason,
  Phone,
  PhoneTag,
  PrivacyStatus,
  PushChallengeData,
  Reason,
  RecoverOptions,
  RecoverRequest,
  RecoveryToken,
  Redirect,
  RequireMfaData,
  SecondaryEmail,
  SendActivationEmailOptions,
  SendActivationEmailRequest,
  SendActivationEmailResponse,
  SendRecoveryEmailOptions,
  SendRecoveryEmailRequest,
  SendRecoveryEmailResponse,
  StateMachineResponse,
  StateMachineResponseStateDataOneOf,
  StateType,
  Status,
  StatusName,
  StatusV2,
  TenantType,
  V1CustomValue,
  V1CustomValueValueOneOf,
  V1Factor,
  V1ListValue,
  V1MapValue,
  VerificationChallenge,
  VerificationChallengeFactorChallengeDataOneOf,
  WebAuthnChallengeData,
} from './iam-recovery-v1-recovery-recovery.universal.js';
